// Optional Collective Intelligence invitation — shown only where CI consent is offered (`available`).
// Both choices start off. Switches and "Accept all" only select; Continue saves each selected option as its own
// record (onboarding card version, linked to the full Learn more disclosure). "Not now" records nothing.
import { useState } from 'react';
import { BarChart3, Layers, ChevronRight, Info, Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import { CI_COPY } from '../components/CollectiveConsent';
import { INVITE_COPY, GOLD, IVORY } from './ciInviteCopy';
import CIConsentSheet from './CIConsentSheet';

const ICONS = { t1: BarChart3, t2: Layers };

const Toggle = ({ on, disabled, onClick, testId, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={onClick}
    data-testid={testId}
    className="relative h-8 w-[3.4rem] shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50"
    style={{ background: on ? GOLD : 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.08)' }}>
    <span className="absolute top-1/2 h-6 w-6 -translate-y-1/2 rounded-full shadow-md transition-transform duration-200"
      style={{ left: 3, background: on ? '#14110B' : '#D9D6D0', transform: `translate(${on ? '1.4rem' : '0'}, -50%)` }} />
  </button>
);

const TierRow = ({ tier, copy, on, busy, onToggle, onLearn }) => {
  const Icon = ICONS[tier];
  return (
    <div className="flex items-start gap-4 rounded-[22px] p-4 sm:p-5" data-testid={`ci-invite-card-${tier}`}
      style={{ border: `1px solid ${on ? 'rgba(232,197,143,0.45)' : 'rgba(255,255,255,0.09)'}`,
        background: on ? 'rgba(232,197,143,0.06)' : 'rgba(14,17,30,0.72)', transition: 'border-color 200ms ease, background-color 200ms ease' }}>
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
        style={{ background: 'rgba(232,197,143,0.08)', border: '1px solid rgba(232,197,143,0.25)' }}>
        <Icon className="h-6 w-6" style={{ color: GOLD }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[1.02rem] font-semibold leading-snug" style={{ color: IVORY }}>{copy[tier].title}</p>
        <p className="mt-1 text-[0.86rem] leading-relaxed" style={{ color: 'rgba(244,239,231,0.68)' }}>{copy[tier].line}</p>
        <button type="button" onClick={onLearn} data-testid={`ci-invite-learn-${tier}`}
          className="-my-1 mt-1 inline-flex min-h-[40px] items-center gap-0.5 text-[0.86rem] transition-opacity hover:opacity-80" style={{ color: GOLD }}>
          {copy.learnMore} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <Toggle on={on} disabled={busy} onClick={onToggle} testId={`ci-invite-toggle-${tier}`} label={copy[tier].title} />
    </div>
  );
};

export const StepInvite = ({ lang, consent, onContinue, onBack }) => {
  const key = lang === 'es' ? 'es' : 'en';
  const copy = INVITE_COPY[key];
  const ci = CI_COPY[key];
  const { state, error, change } = consent;
  // On this screen the switches only select; Continue saves each choice as its own record.
  const [picked, setPicked] = useState(null);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const isOn = (t) => !!state?.[t]?.enabled;
  const sel = picked || { t1: isOn('t1'), t2: isOn('t2') };
  const toggle = (t) => setPicked({ ...sel, [t]: !sel[t] });
  const acceptAll = () => setPicked({ t1: true, t2: true });

  const save = async () => {
    setSaving(true);
    let ok = true;
    for (const t of ['t1', 't2']) {
      if (sel[t] && !isOn(t)) {
        ok = (await change(t, true, { version: state?.[t]?.onboarding_version || '', locale: key })) && ok;
      } else if (!sel[t] && isOn(t)) {
        ok = (await change(t, false)) && ok;
      }
    }
    setSaving(false);
    if (ok) onContinue();
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden" data-testid="onboarding-ci-invite"
      style={{ background: 'radial-gradient(120% 60% at 0% 10%, rgba(40,80,160,0.22), transparent 60%), radial-gradient(90% 50% at 100% 20%, rgba(120,60,170,0.18), transparent 60%), #05070F' }}>
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-[34rem] flex-col px-5 pb-8 pt-6 sm:px-6">
        <button type="button" onClick={onBack} data-testid="onboarding-ci-invite-back"
          className="inline-flex items-center gap-1.5 self-start text-[0.84rem] text-white/55 transition-colors hover:text-white/85">
          <ArrowLeft className="h-4 w-4" /> {copy.back}
        </button>
        <img src="/auth/v1/arvexis-logo-gold.webp" alt="Arvexis" width="132" height="55" className="mx-auto mt-2 w-[132px] select-none" draggable="false" />
        <h1 className="mt-6 text-center text-[1.9rem] font-semibold leading-tight tracking-[-0.01em] sm:text-[2.2rem]"
          style={{ color: IVORY }} data-testid="onboarding-ci-invite-headline">
          {copy.headline[0]}<span style={{ color: GOLD }}>{copy.headline[1]}</span>{copy.headline[2]}
        </h1>
        <p className="mt-3 text-center text-[0.95rem] leading-relaxed" style={{ color: 'rgba(244,239,231,0.78)' }}>
          {copy.sub} <span className="whitespace-nowrap">{copy.control}</span>
        </p>

        <>
          <button type="button" onClick={acceptAll} disabled={saving} data-testid="onboarding-ci-accept-all"
            className="mt-7 flex h-14 w-full items-center justify-center gap-2.5 rounded-full text-[1.05rem] font-semibold shadow-[0_10px_40px_rgba(232,197,143,0.18)] transition-transform duration-150 active:scale-[0.985]"
            style={{ background: `linear-gradient(180deg, #F3D9A6, ${GOLD} 55%, #C9A266)`, color: '#14110B' }}>
            <Sparkles className="h-5 w-5" /> {copy.acceptAll}
          </button>
          <p className="mt-2 text-center text-[0.82rem] text-white/60">{copy.acceptAllHint}</p>
        </>
        <div className="my-5 flex items-center gap-4">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-[0.84rem]" style={{ color: GOLD }}>{copy.or}</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="flex flex-col gap-3">
          {['t1', 't2'].map((t) => (
            <TierRow key={t} tier={t} copy={copy} on={sel[t]} busy={saving}
              onToggle={() => toggle(t)} onLearn={() => setInfo(t)} />
          ))}
        </div>
        {error && <p className="mt-3 text-sm text-[#F2A7A7]" data-testid="onboarding-ci-invite-error">{error}</p>}

        <div className="mt-5 flex items-start gap-3 px-1" data-testid="onboarding-ci-invite-note">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-white/55" />
          <p className="text-[0.82rem] leading-relaxed text-white/60">{copy.info}</p>
        </div>

        <div className="mt-auto flex flex-col items-center gap-2 pt-7">
          <button type="button" onClick={save} disabled={saving} data-testid="onboarding-ci-invite-continue"
            className="h-14 w-full rounded-full text-[1.05rem] font-semibold transition-colors duration-150 hover:bg-white/[0.03] disabled:opacity-60"
            style={{ color: GOLD, border: '1px solid rgba(232,197,143,0.55)', background: 'rgba(10,12,22,0.6)' }}>
            {saving ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : copy.continue}
          </button>
          <button type="button" onClick={onContinue} disabled={saving} data-testid="onboarding-ci-invite-not-now"
            className="h-11 px-6 text-[0.98rem] text-white/70 transition-colors hover:text-white">
            {copy.notNow}
          </button>
        </div>
      </div>
      <CIConsentSheet tier={info} onClose={() => setInfo(null)} ci={ci} copy={copy} />
    </div>
  );
};

export default StepInvite;
